import { Component } from 'react';
import { debounce, isNil, noop } from 'lodash';

import { Search as SearchInput, SearchIcon, SearchWrapper } from './Search.styles';
import { ACTIVE } from 'styles/commonClasses';
import { PropsOf } from 'utils/types.utils';

interface Props extends PropsOf<typeof SearchInput> {
  children?: React.ReactNode;
  searchQuery?: string | null;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void | null;
  onChangeSearchQuery: (query: string) => void;
  onChangeActiveState: (active: boolean) => void;
}

interface State {
  active: boolean;
  searchQuery: string;
  stashedProps: {
    searchQuery?: string | null;
  };
}

export class Search extends Component<Props, State> {
  static defaultProps: Partial<Props> = {
    children: null,
    searchQuery: '',
    onKeyDown: null,
    onChangeSearchQuery: noop,
    onChangeActiveState: noop,
  };

  state: State = {
    active: false,
    searchQuery: '',
    stashedProps: {
      searchQuery: this.props.searchQuery,
    },
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State): Partial<State> | null {
    const { searchQuery } = nextProps;

    let newState: Partial<State> = {
      stashedProps: {
        searchQuery,
      },
    };

    // Clear the input by passing 'null'
    if (isNil(searchQuery) && !isNil(prevState.stashedProps.searchQuery)) {
      newState = {
        ...newState,
        searchQuery: '',
      };
    }

    return newState;
  }

  debouncedOnChangeSearchQuery = debounce(this.props.onChangeSearchQuery, 200);

  handleOnFocus = () => {
    this.setState({
      active: true,
    });

    this.props.onChangeActiveState(true);
  };

  handleOnBlur = () => {
    this.setState({
      active: false,
    });

    this.props.onChangeActiveState(false);
  };

  handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.debouncedOnChangeSearchQuery(event.currentTarget.value);

    this.setState({ searchQuery: event.currentTarget.value });
  };

  render() {
    const { onKeyDown, ...rest } = this.props;
    return (
      <SearchWrapper className={this.state.active ? ACTIVE : undefined} data-testid="search-box">
        <SearchIcon className="icon-search" />
        <SearchInput
          {...rest}
          type="text"
          name="query"
          value={this.state.searchQuery}
          onChange={this.handleOnChange}
          onKeyDown={onKeyDown}
          onFocus={this.handleOnFocus}
          onBlur={this.handleOnBlur}
        />
        {this.props.children}
      </SearchWrapper>
    );
  }
}

export default Search;
